import { LayoutMetadataForFit } from './compute-fit-box';

export type LayoutMetadata = LayoutMetadataForFit & {
  contentAlignment?: string;
};

// The only metadata.json schema this code understands. html-layout-library
// adds fields within a version (ignored here) and bumps this on a breaking
// change, in which case every layout reads as "no data" rather than being
// misinterpreted.
export const SUPPORTED_SCHEMA_VERSION = 1;

const REPEAT_DIRECTIONS = ['horizontal', 'vertical'];

export interface ParsedLayoutMetadataFile {
  // Only entries that passed validation. A layout missing from here is "no
  // data", whether it was absent from the file or its entry was malformed.
  layouts: Map<string, LayoutMetadata>;
  // Why anything was dropped, for a single diagnostic log. Empty when the
  // whole file was usable.
  problems: string[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

// Returns null for anything that isn't a fully valid entry — an entry is
// never partially trusted, since a half-valid one (say, a bad `repeating`)
// would size a box differently than the layout renders. Fields apps doesn't
// know are ignored, so the file can grow without breaking this reader.
function parseEntry(raw: unknown): LayoutMetadata | null {
  if (!isPlainObject(raw)) {
    return null;
  }

  const { baseWidth, baseHeight, enforceBaseMinimum, repeating } = raw;
  const contentAlignment = raw['contentAlignment'];

  if (!isPositiveFinite(baseWidth) || !isPositiveFinite(baseHeight)) {
    return null;
  }

  const entry: LayoutMetadata = { baseWidth, baseHeight };

  if (enforceBaseMinimum !== undefined) {
    if (typeof enforceBaseMinimum !== 'boolean') {
      return null;
    }
    entry.enforceBaseMinimum = enforceBaseMinimum;
  }

  if (repeating !== undefined) {
    if (
      !isPlainObject(repeating) ||
      typeof repeating['countProperty'] !== 'string' ||
      repeating['countProperty'].trim() === '' ||
      !REPEAT_DIRECTIONS.includes(repeating['direction'] as string)
    ) {
      return null;
    }
    entry.repeating = {
      countProperty: repeating['countProperty'],
      direction: repeating['direction'] as string,
    };
  }

  if (contentAlignment !== undefined) {
    if (typeof contentAlignment !== 'string') {
      return null;
    }
    entry.contentAlignment = contentAlignment;
  }

  return entry;
}

// Validates a parsed html-layout-library metadata.json defensively. Never
// throws: an unusable file or entry just yields fewer layouts, which callers
// already treat as "use the default box".
export function parseLayoutMetadataFile(
  raw: unknown,
): ParsedLayoutMetadataFile {
  const layouts = new Map<string, LayoutMetadata>();
  const problems: string[] = [];

  if (!isPlainObject(raw)) {
    problems.push('metadata file is not a JSON object');
    return { layouts, problems };
  }

  if (raw['schemaVersion'] !== SUPPORTED_SCHEMA_VERSION) {
    problems.push(
      `unsupported schemaVersion ${JSON.stringify(raw['schemaVersion'])} (expected ${SUPPORTED_SCHEMA_VERSION})`,
    );
    return { layouts, problems };
  }

  const rawLayouts = raw['layouts'];
  if (!isPlainObject(rawLayouts)) {
    problems.push('metadata file has no "layouts" object');
    return { layouts, problems };
  }

  Object.entries(rawLayouts).forEach(([layoutId, rawEntry]) => {
    const entry = parseEntry(rawEntry);
    if (entry) {
      layouts.set(layoutId, entry);
    } else {
      problems.push(`ignored malformed entry for "${layoutId}"`);
    }
  });

  return { layouts, problems };
}
